import React, { useState, useEffect, useCallback, useRef } from 'react';
import styles from '../css/clinical.module.css';
import '../css/pirogov-dev.css';

import { captions, MAX_POLL_COUNT } from './pirogov-dev/constants';
import { getCookie, setCookieValue, downloadFile } from './pirogov-dev/utils';
import {
  TypewriterText,
  DownloadBtn, InstructionsModal,
} from './pirogov-dev/components';
import useDualCtrlNav from './pirogov-dev/useDualCtrlNav';
import trashIcon from '../img/agent/trash.svg';
import plusIcon from '../img/agent/plus.svg';
import menuIcon from '../img/agent/menu.svg';

// ── Константы Anatomage ──

const bodyOptions = [
  { value: "Victor.vpf", label: "Victor" },
  { value: "Vicky.vpf", label: "Vicky" },
  { value: "Hans.vpf", label: "Hans" },
  { value: "Carl.vpf", label: "Carl" },
  { value: "Carla.vpf", label: "Carla" },
];

const modeOptions = [
  { value: "standard", label: "Обычный" },
  { value: "clinical", label: "Клиницист" },
  { value: "surgical", label: "Хирург" },
];

const instructions = {
  standard: {
    title: "📖 Обычный режим (Образовательный)",
    description: "Для изучения анатомии, структуры органов и пространственных взаимосвязей.",
    examples: [
      { formula: "Внутренняя анатомия [Органа]", items: ["Внутренняя анатомия головного мозга", "Внутренняя анатомия сердца"] },
      { formula: "[Система] [Области/Органа]", items: ["Сосуды печени", "Нервы предплечья"] },
      { formula: "Используйте общие понятия", items: ["Органы мочевыделительной системы", "Органы пищеварительной системы"] },
    ],
    colorInfo: "Структуры окрашиваются случайным образом для легкого различия. При необходимости отключите окрашивание в Расширенных настройках.",
  },
  clinical: {
    title: "🩺 Клинический режим",
    description: "Демонстрирует причинно-следственные связи между анатомическими структурами.",
    examples: [
      { formula: "Почему [Симптом] из-за [Причины]", items: ["Почему синдром запястного канала вызывает онемение?"] },
      { formula: "[Причина], вызывающая [Эффект]", items: ["Грыжа диска, вызывающая компрессию нервного корешка"] },
      { formula: "Механизм [Синдрома/Состояния]", items: ["Механизм синдрома верхней апертуры грудной клетки"] },
    ],
    colorInfo: "Причинная структура = зеленая, Пораженная структура = полупрозрачная синяя. Это нельзя отключить.",
  },
  surgical: {
    title: "🔪 Хирургический режим",
    description: "Планирование хирургических вмешательств, коридоров доступа и структур, подверженных риску.",
    examples: [
      { formula: "[Тип доступа] к [Целевой структуре]", items: ["Трансназальный эндоскопический доступ к аденоме гипофиза"] },
      { formula: "[Процедура] с сохранением [Структур риска]", items: ["Тиреоидэктомия с сохранением возвратного гортанного нерва"] },
    ],
    colorInfo: "Целевая структура = зеленая для точной идентификации. Это нельзя отключить.",
  },
};

// ── Приветственное сообщение ──
const WelcomeMessageAnatomage = () => (
  <div className="pd-welcome">
    <div className="pd-welcome__bubble">
      <div className="pd-welcome__greeting">Привет! 👋</div>
      <div>Я — AI Brain, ваш помощник для создания анатомических 3D-сцен на базе Anatomage.</div>
      <div>Вот что я умею:</div>
      <ul className="pd-welcome__list">
        <li>Работать в режимах: <b>обычный</b>, <b>клиницист</b> и <b>хирург</b></li>
        <li>Создавать анатомические сцены по текстовому описанию</li>
      </ul>
      <div className="pd-welcome__hint">Опишите, что хотите увидеть, и я создам сцену для вас.</div>
    </div>
  </div>
);

const AnatomyAssistantAnatomageSimple = () => {
  useDualCtrlNav();
  const [query, setQuery] = useState('');
  const [selectedBody, setSelectedBody] = useState(bodyOptions[0].value);
  const [selectedMode, setSelectedMode] = useState(modeOptions[0].value);
  const [loading, setLoading] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [scenePlanningInfo, setScenePlanningInfo] = useState(null);
  const [error, setError] = useState(null);
  const [captionIndex, setCaptionIndex] = useState(0);
  const [showInstructions, setShowInstructions] = useState(false);
  const [disabledColors, setDisabledColors] = useState(false); // kept for API compat, always false
  const [apiToken, setApiToken] = useState(() => getCookie('api_token') || '');
  const [requestsRemaining, setRequestsRemaining] = useState(null);
  const [showTokenInput, setShowTokenInput] = useState(() => !getCookie('api_token'));
  const [currentPhase, setCurrentPhase] = useState(null);
  const [currentAgent, setCurrentAgent] = useState(null);

  const [showPlanningDetails, setShowPlanningDetails] = useState(true);
  const [savedPlanningDetails, setSavedPlanningDetails] = useState([]);

  // История всех завершённых генераций (для сериализации чата)
  const [generationHistory, setGenerationHistory] = useState([]);

  const [thinkingDots, setThinkingDots] = useState(1);

  const [showMobileMenu, setShowMobileMenu] = useState(false);

  // ── История чатов (авто-сохранение без UI) ──
  const chatIdRef = useRef(null);
  const prevSaveKeyRef = useRef('');

  const currentInstruction = instructions[selectedMode];
  const canDisableColors = selectedMode === 'standard';

  const isInChatMode = !!(downloadUrl || loading);

  const handleTokenSave = (token) => {
    const tokenToSave = token.trim();
    if (!tokenToSave) { setError('Пожалуйста, введите токен'); return; }
    setCookieValue('api_token', tokenToSave);
    setApiToken(tokenToSave);
    setShowTokenInput(false);
    setError(null);

    const loadStats = async () => {
      try {
        const response = await fetch('/api/v1/token-stats/', {
          method: 'GET',
          headers: { 'X-CSRFToken': getCookie('csrftoken'), 'X-Auth-Token': tokenToSave },
          credentials: 'include',
        });
        if (response.ok) {
          const data = await response.json();
          if (data.rate_limit && data.rate_limit.remaining !== undefined) setRequestsRemaining(data.rate_limit.remaining);
        } else if (response.status === 401) {
          setError('❌ Неверный токен. Проверьте и попробуйте снова.');
          setShowTokenInput(true); setApiToken(''); setCookieValue('api_token', '', -1);
        } else if (response.status === 502) {
          setError('❌ Ошибка сервера (502). Проверьте логи сервера.');
        } else {
          const errorData = await response.json().catch(() => ({}));
          setError(`❌ Ошибка при загрузке статистики (${response.status}): ${errorData.error || 'Unknown error'}`);
        }
      } catch (err) {
        setError(`❌ Ошибка при загрузке статистики: ${err.message}`);
      }
    };
    loadStats();
  };

  const handleTokenReset = () => {
    setApiToken(''); setCookieValue('api_token', '', -1);
    setShowTokenInput(true); setRequestsRemaining(null);
  };

  useEffect(() => {
    if (loading) {
      const interval = setInterval(() => setCaptionIndex((prev) => (prev + 1) % captions.length), 3000);
      return () => clearInterval(interval);
    }
  }, [loading]);

  useEffect(() => {
    if (loading) {
      const interval = setInterval(() => setThinkingDots(d => d >= 3 ? 1 : d + 1), 500);
      return () => clearInterval(interval);
    } else { setThinkingDots(1); }
  }, [loading]);

  useEffect(() => {
    const loadTokenStats = async () => {
      if (apiToken && !requestsRemaining) {
        try {
          const response = await fetch('/api/v1/token-stats/', {
            method: 'GET',
            headers: { 'X-CSRFToken': getCookie('csrftoken'), 'X-Auth-Token': apiToken },
            credentials: 'include',
          });
          if (response.ok) {
            const data = await response.json();
            if (data.rate_limit && data.rate_limit.remaining !== undefined) setRequestsRemaining(data.rate_limit.remaining);
          } else if (response.status === 401) {
            setShowTokenInput(true);
          } else if (response.status === 502) {
            setError('❌ Ошибка сервера (502). Проверьте логи на сервере.');
          } else {
            const errorData = await response.json().catch(() => ({}));
            setError(`❌ Ошибка ${response.status}: ${errorData.error || 'Unknown error'}`);
          }
        } catch (err) { setError(`❌ Ошибка сети: ${err.message}`); }
      }
    };
    loadTokenStats();
  }, [apiToken]);

  const handleDownload = useCallback(async (url) => {
    try { await downloadFile(url, apiToken); }
    catch (err) { setError('Ошибка при скачивании файла.'); }
  }, [apiToken]);

  const pollCountRef = React.useRef(0);

  const pollTaskStatus = (taskId) => {
    return new Promise((resolve) => {
      const poll = async () => {
        pollCountRef.current += 1;
        if (pollCountRef.current > MAX_POLL_COUNT) {
          setError('Превышено время ожидания ответа. Попробуйте ещё раз.');
          setLoading(false); setCurrentPhase(null); setCurrentAgent(null);
          resolve(); return;
        }
        try {
          const response = await fetch(`/api/v1/task-status/${taskId}/`, {
            method: 'GET',
            headers: { 'X-CSRFToken': getCookie('csrftoken'), 'X-Auth-Token': apiToken },
            credentials: 'include',
          });
          if (!response.ok) throw new Error(`Ошибка сервера: ${response.status}`);
          const data = await response.json();

          if (data.status === 'SUCCESS') {
            const result = data.result;
            if (result.download_url) {
              setDownloadUrl(result.download_url);
              const fullScenePlanningInfo = {
                ...result.scene_planning_info,
                rationale: result.ai_response?.rationale,
                applied_mode: result.ai_response?.applied_mode,
                special_rules: result.ai_response?.special_rules,
              };
              setScenePlanningInfo(fullScenePlanningInfo);
              if (result.rate_limit && result.rate_limit.remaining !== undefined) {
                setRequestsRemaining(result.rate_limit.remaining);
              }
            } else {
              setError(result.error || result.details || 'Не удалось получить ссылку для скачивания.');
            }
            setLoading(false); setCurrentPhase(null); setCurrentAgent(null);
            resolve();
          } else if (data.status === 'FAILURE') {
            setError(data.error || 'Произошла ошибка при обработке запроса.');
            setLoading(false); setCurrentPhase(null); setCurrentAgent(null);
            resolve();
          } else {
            if (data.progress) {
              if (data.progress.phase) setCurrentPhase(data.progress.phase);
              if (data.progress.agent) setCurrentAgent(data.progress.agent);
            }
            setTimeout(poll, 2000);
          }
        } catch (err) {
          setError('Ошибка при проверке статуса задачи.');
          setLoading(false); setCurrentPhase(null); setCurrentAgent(null);
          resolve();
        }
      };
      poll();
    });
  };

  const doGenerate = async (queryText) => {
    pollCountRef.current = 0;

    // Сохраняем текущую завершённую генерацию в историю перед сбросом
    if (downloadUrl) {
      setGenerationHistory(prev => [...prev, {
        query,
        downloadUrl,
        scenePlanningInfo,
        savedPlanningDetails,
      }]);
    }

    setLoading(true);
    setError(null);
    setDownloadUrl(null);
    setScenePlanningInfo(null);
    setSavedPlanningDetails([]);
    setCurrentPhase(null);
    setCurrentAgent(null);

    try {
      const response = await fetch('/api/v1/anatomage-query/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCookie('csrftoken'),
          'X-Auth-Token': apiToken,
        },
        body: JSON.stringify({
          query: queryText,
          body: selectedBody,
          mode: selectedMode,
        }),
        credentials: 'include',
      });

      const text = await response.text();
      let data;
      try { data = JSON.parse(text); }
      catch (jsonError) {
        setError(`Ошибка сервера (${response.status}): ${text.substring(0, 200)}`);
        setLoading(false); return;
      }

      if (response.status === 401) {
        setError('❌ Неверный токен. Проверьте и попробуйте снова.');
        setShowTokenInput(true); setLoading(false);
      } else if (response.status === 429) {
        setError('⚠️ Лимит запросов на месяц исчерпан. Попробуйте в следующем месяце.');
        setLoading(false);
      } else if (response.status === 202 && data.task_id) {
        pollTaskStatus(data.task_id);
      } else if (response.ok && data.download_url) {
        setDownloadUrl(data.download_url);
        const fullScenePlanningInfo = {
          ...data.scene_planning_info,
          rationale: data.ai_response?.rationale,
          applied_mode: data.ai_response?.applied_mode,
          special_rules: data.ai_response?.special_rules,
        };
        setScenePlanningInfo(fullScenePlanningInfo);
        if (data.rate_limit && data.rate_limit.remaining !== undefined) setRequestsRemaining(data.rate_limit.remaining);
        setLoading(false);
      } else {
        setError(data.error || data.details || 'Не удалось получить ссылку для скачивания.');
        setLoading(false);
      }
    } catch (err) {
      setError('Ошибка подключения к серверу.');
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    if (!apiToken.trim()) { setError('Пожалуйста, введите API токен'); return; }
    await doGenerate(query);
  };

  // ── Сериализация и авто-сохранение чата ──
  const serializeChatState = () => {
    const messages = [];

    // Предыдущие завершённые генерации
    generationHistory.forEach(gen => {
      messages.push({ role: 'user', type: 'query', text: gen.query });
      messages.push({
        role: 'assistant', type: 'generation',
        download_url: gen.downloadUrl,
        scene_planning_info: gen.scenePlanningInfo,
        saved_planning_details: gen.savedPlanningDetails,
      });
    });

    // Текущая генерация
    messages.push({ role: 'user', type: 'query', text: query });

    if (downloadUrl) {
      messages.push({
        role: 'assistant', type: 'generation',
        download_url: downloadUrl,
        scene_planning_info: scenePlanningInfo,
        saved_planning_details: savedPlanningDetails,
      });
    }

    return messages;
  };

  const saveChat = async () => {
    if (!apiToken || !downloadUrl) return;
    try {
      const messages = serializeChatState();
      const resp = await fetch('/api/v1/chats/save/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCookie('csrftoken'),
          'X-Auth-Token': apiToken,
        },
        body: JSON.stringify({
          chat_id: chatIdRef.current || undefined,
          body: selectedBody,
          mode: selectedMode,
          title: (generationHistory.length > 0
            ? generationHistory[0].query
            : query || ''
          ).substring(0, 120),
          messages,
        }),
        credentials: 'include',
      });
      const data = await resp.json();
      if (data.success && data.chat_id) {
        chatIdRef.current = data.chat_id;
      }
    } catch (err) {
      console.error('Auto-save failed:', err);
    }
  };

  useEffect(() => {
    if (!downloadUrl || !apiToken || loading) return;
    const saveKey = `${downloadUrl}`;
    if (saveKey === prevSaveKeyRef.current) return;
    prevSaveKeyRef.current = saveKey;
    const timer = setTimeout(() => saveChat(), 800);
    return () => clearTimeout(timer);
  }, [downloadUrl, loading]);

  const getSceneValue = (key) => {
    if (!scenePlanningInfo) return undefined;
    if (scenePlanningInfo[key]) return scenePlanningInfo[key];
    if (scenePlanningInfo.rationale && typeof scenePlanningInfo.rationale !== 'string') {
      return scenePlanningInfo.rationale[key];
    }
    return undefined;
  };

  return (
    <>
      {/* Instructions Modal */}
      {showInstructions && (
        <InstructionsModal
          selectedMode={selectedMode}
          onSelectMode={setSelectedMode}
          currentInstruction={currentInstruction}
          onClose={() => setShowInstructions(false)}
        />
      )}

      <div className={`pd-root${downloadUrl ? ' pd-root--chat' : ' pd-root--form'}`}>

      <div className={styles.backgroundSvg} />

        <div className="pd-deco-circle pd-deco-circle--top" />
        <div className="pd-deco-circle pd-deco-circle--bottom" />

        <div className="pd-container">

          <div className="pd-header">
            <div className="pd-header__row">
              <h1 className="pd-header__title"><a href="/" style={{ color: 'inherit', textDecoration: 'none' }}>🧠 AI Brain</a></h1>

              <button
                type="button"
                className="pd-menu-btn"
                onClick={() => setShowMobileMenu(prev => !prev)}
                aria-label="Меню"
              >
                <img src={menuIcon} alt="меню" className="pd-btn-icon" />
              </button>

              <div className={`pd-header-nav${showMobileMenu ? ' pd-header-nav--open' : ''}`}>

                <button
                  type="button"
                  onClick={() => window.location.reload()}
                  className="pd-header-btn"
                >
                  <img src={plusIcon} alt="" className="pd-btn-icon" />
                  <span className="pd-btn-label">Новый запрос</span>
                </button>

                {apiToken && (
                  <button
                    type="button"
                    onClick={() => setShowTokenInput(prev => !prev)}
                    className="pd-token-btn"
                    title="API токен"
                  >
                    🔑
                  </button>
                )}

                {/* Выбор тела */}
                <div className="pd-body-toggle">
                  {bodyOptions.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => {
                        if (opt.value !== selectedBody) {
                          if (isInChatMode) {
                            window.location.reload();
                          } else {
                            setSelectedBody(opt.value);
                          }
                        }
                      }}
                      className={`pd-body-toggle__btn${selectedBody === opt.value ? ' pd-body-toggle__btn--active' : ' pd-body-toggle__btn--inactive'}`}
                    >
                      <span className="pd-btn-label">{opt.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="pd-header__right">
                {apiToken && requestsRemaining !== null && (
                  <div className="pd-requests-badge">
                    <span className="pd-requests-badge__label">Запросов осталось:</span>
                    <span className="pd-requests-badge__count">{requestsRemaining}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Плашка токена */}
            {showTokenInput && (
              <div className="pd-token-panel">
                <div className="pd-token-panel__header">
                  <span className="pd-token-panel__icon">🔑</span>
                  <div className="pd-token-panel__title">
                    API Токен {apiToken ? '(замена)' : '(обязателен)'}
                  </div>
                </div>
                <input
                  type="password"
                  value={apiToken}
                  onChange={(e) => setApiToken(e.target.value)}
                  placeholder="Вставьте ваш API токен здесь..."
                  className="pd-token-input"
                />
                <div className="pd-token-panel__actions">
                  {apiToken && (
                    <button type="button" onClick={() => { handleTokenSave(apiToken); }} className="pd-token-save">
                      ✓ Сохранить
                    </button>
                  )}
                  {apiToken && (
                    <button type="button" onClick={handleTokenReset} className="pd-token-delete">
                      <img src={trashIcon} alt="trash" style={{ width: 16, height: 16 }} /> Удалить
                    </button>
                  )}
                  {!apiToken && (
                    <button
                      type="button"
                      onClick={() => { if (!apiToken.trim()) { setError('Пожалуйста, введите токен'); return; } handleTokenSave(apiToken); }}
                      className="pd-token-apply"
                    >
                      ✓ Применить токен
                    </button>
                  )}
                </div>
                <div className="pd-token-panel__hint">
                  💡 Токен будет сохранён в cookies браузера и не потребуется вводить заново.
                </div>
              </div>
            )}
          </div>

          {/* Форма ввода */}
          {!downloadUrl && !loading && (
          <div className="pd-form-center">
          <div className="pd-form-inner">

            <WelcomeMessageAnatomage />

            <form onSubmit={handleSubmit}>

              <div className="pd-form__heading">
                Что показать?
              </div>

              <div className="pd-form__subheading">
                Опишите текстом, что вы хотите увидеть.
              </div>

              <div className="pd-form__textarea-wrap">
                <div className="pd-form__textarea-relative">
                  <textarea
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="например, внутренняя анатомия мозга"
                    rows={3}
                    disabled={!!downloadUrl}
                    className="pd-initial-textarea"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSubmit(e); }
                    }}
                  />
                  <div className="pd-initial-buttons">
                    <button
                      type="submit"
                      disabled={loading || !apiToken || !!downloadUrl}
                      className="pd-initial-send"
                      title={!apiToken ? 'Сначала введите токен' : 'Сгенерировать'}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="19" x2="12" y2="5"/><polyline points="5 12 12 5 19 12"/></svg>
                    </button>
                  </div>
                </div>
              </div>

              {/* Checkbox: Отключить окрашивание — removed, colors always enabled */}

              {/* Error */}
              {error && (
                <div className="pd-error-msg">⚠️ {error}</div>
              )}

            </form>

          </div>
          </div>
          )}

          {/* Results Section */}
          {(downloadUrl || loading) && (
            <div className="pd-results">

              <div className={`pd-messages${isInChatMode ? ' pd-messages--with-input' : ''}`}>

                <div className="pd-msg-group">
                  <div className="pd-msg-right">
                    <div className="pd-user-bubble">{query}</div>
                  </div>

                  {downloadUrl && (
                  <div className="pd-scene-response">

                    {savedPlanningDetails.length > 0 && (
                      <div className="pd-planning">
                        <div className="pd-planning__header" onClick={() => setShowPlanningDetails(!showPlanningDetails)}>
                          <span>Детали планирования сцены</span>
                          <span className={`pd-planning__chevron${showPlanningDetails ? ' pd-planning__chevron--open' : ''}`}>▾</span>
                        </div>
                        {showPlanningDetails && (
                          <div className="pd-planning__body">
                            {savedPlanningDetails.map((t, idx) => (
                              <div key={idx} className="pd-planning__section">
                                <div className="pd-planning__label">{t.label}</div>
                                <div className="pd-planning__value">{t.text}</div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Scene Planning Info — inline (Anatomage style) */}
                    {scenePlanningInfo && Object.keys(scenePlanningInfo).length > 0 && (
                      <div className="pd-planning">
                        <div className="pd-planning__header" onClick={() => setShowPlanningDetails(!showPlanningDetails)}>
                          <span>🎯 Детали планирования сцены</span>
                          <span className={`pd-planning__chevron${showPlanningDetails ? ' pd-planning__chevron--open' : ''}`}>▾</span>
                        </div>
                        {showPlanningDetails && (
                          <div className="pd-planning__body">
                            {scenePlanningInfo.rationale && (typeof scenePlanningInfo.rationale === 'string' ? scenePlanningInfo.rationale : scenePlanningInfo.rationale?.name) && (
                              <div className="pd-planning__section">
                                <div className="pd-planning__label">🎓 Цель исследования</div>
                                <div className="pd-planning__value">
                                  {typeof scenePlanningInfo.rationale === 'string' ? scenePlanningInfo.rationale : scenePlanningInfo.rationale?.name}
                                </div>
                              </div>
                            )}
                            {scenePlanningInfo.applied_mode && (
                              <div className="pd-planning__section">
                                <span className="pd-planning__label">⚙️ Режим: </span>
                                <span className="pd-planning__mode-value">{scenePlanningInfo.applied_mode}</span>
                              </div>
                            )}
                            {scenePlanningInfo.special_rules && (
                              <div className="pd-planning__section">
                                <div className="pd-planning__label">⚡ Специальные правила</div>
                                <div className="pd-planning__value">
                                  {typeof scenePlanningInfo.special_rules === 'string' ? scenePlanningInfo.special_rules : JSON.stringify(scenePlanningInfo.special_rules, null, 2)}
                                </div>
                              </div>
                            )}
                            {getSceneValue('visualization_intent') && (
                              <div className="pd-planning__section">
                                <div className="pd-planning__label">🔬 Намерение визуализации</div>
                                <div className="pd-planning__value">{getSceneValue('visualization_intent')}</div>
                              </div>
                            )}
                            {getSceneValue('focus_choice') && (
                              <div className="pd-planning__section">
                                <div className="pd-planning__label">🎯 Целевые структуры</div>
                                <div className="pd-planning__value">{getSceneValue('focus_choice')}</div>
                              </div>
                            )}
                            {getSceneValue('context_choice') && (
                              <div className="pd-planning__section">
                                <div className="pd-planning__label">📦 Контекстные структуры</div>
                                <div className="pd-planning__value">{getSceneValue('context_choice')}</div>
                              </div>
                            )}
                            {getSceneValue('view_recommendation') && (
                              <div className="pd-planning__section">
                                <div className="pd-planning__label">👁️ Рекомендации для демонстрации</div>
                                <div className="pd-planning__value">{getSceneValue('view_recommendation')}</div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    <div className="pd-status-msg">✅ Сцена сгенерирована</div>

                    <DownloadBtn onClick={() => handleDownload(downloadUrl)} initial />
                  </div>
                  )}
                </div>

                {/* Thinking indicator */}
                {loading && (
                  <div className="pd-msg-left pd-thinking">
                    {currentPhase ? currentPhase + '.'.repeat(thinkingDots) : 'Думает' + '.'.repeat(thinkingDots)}
                  </div>
                )}

              </div>

              {/* Форма для новой генерации (после готовой сцены) */}
              {downloadUrl && !loading && <div className="pd-fixed-bottom">
                <form onSubmit={(e) => { e.preventDefault(); if (!query.trim() || !apiToken.trim()) return; doGenerate(query); }}>
                  <div className="pd-input-wrap">
                    <textarea
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      placeholder="Опишите новую сцену..."
                      rows={3}
                      className="pd-chat-textarea"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); if (query.trim() && apiToken.trim()) doGenerate(query); }
                      }}
                    />
                    <div className="pd-input-buttons">
                      <button
                        type="submit"
                        disabled={!query.trim()}
                        className="pd-send-btn"
                        title="Сгенерировать новую сцену"
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="12" y1="19" x2="12" y2="5" />
                          <polyline points="5 12 12 5 19 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </form>
              </div>}

            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default AnatomyAssistantAnatomageSimple;
